import {useSelector} from 'react-redux';

import {isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import {selectSaleVehicle, useGetRentalVehicleQuery} from '@dms/api/vehicle';
import {
  useGetServiceVehicleAdditionalInformationQuery,
  useGetServiceVehicleQuery,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {vehiclesRoutes, testIds} from '@dms/routes';
import {Main, useIsLicenceEnabled} from '@dms/shared';
import {
  VehicleContentDetails,
  VehicleData,
  VehicleFeatures,
  VehicleRental,
  VehicleServiceIntervals,
  VehicleAdditionalInformation,
} from '@dms/vehicle-detail';

import {composePath, buildArray} from 'shared/web';

import type {EagGridMaximization} from 'features/eag-grid';

type VehicleDetailContentProps = {
  maximization?: EagGridMaximization;
  vehicleId: string;
};

export function VehicleDetailContent(props: VehicleDetailContentProps) {
  const {data: saleVehicle} = useSelector(selectSaleVehicle(props.vehicleId));
  const {data: rentalVehicle} = useGetRentalVehicleQuery({vehicleId: props.vehicleId});
  const {data: serviceVehicle} = useGetServiceVehicleQuery({vehicleId: props.vehicleId});

  const isSaleVehicle = isNotNil(saleVehicle);
  const isServiceVehicle = isNotNil(serviceVehicle);
  const isRentalVehicle = isNotNil(rentalVehicle);

  const {isWorkshopModuleEnabled} = useIsLicenceEnabled();

  const {data: additionalInformation} = useGetServiceVehicleAdditionalInformationQuery(
    {serviceVehicleId: serviceVehicle?.serviceVehicleId ?? ''},
    {skip: !isServiceVehicle}
  );

  const navigationItems = buildArray([
    {
      id: 'basicInformation',
      title: i18n.t('general.labels.basicInformation'),
      content: <VehicleContentDetails isSaleVehicle={isSaleVehicle} vehicleId={props.vehicleId} />,
      href: composePath(vehiclesRoutes.detail, {params: {id: props.vehicleId}}),
      'data-testid': testIds.vehicles.detail('basicInformation'),
    },
    {
      id: 'vehicleData',
      title: i18n.t('entity.vehicle.labels.vehicleData'),
      content: (
        <VehicleData
          isSaleVehicle={isSaleVehicle}
          vehicleId={props.vehicleId}
          maximization={props.maximization}
        />
      ),
      href: composePath(vehiclesRoutes.vehicleData, {params: {id: props.vehicleId}}),
      'data-testid': testIds.vehicles.detail('vehicleData'),
    },
  ])
    .when(isSaleVehicle, {
      id: 'features',
      title: i18n.t('entity.vehicle.labels.features'),
      content: <VehicleFeatures vehicleId={props.vehicleId} />,
      href: composePath(vehiclesRoutes.features, {params: {id: props.vehicleId}}),
      'data-testid': testIds.vehicles.detail('features'),
    })
    .when(!isSaleVehicle && isWorkshopModuleEnabled, {
      id: 'features',
      title: i18n.t('entity.vehicle.labels.features'),
      content: <VehicleFeatures vehicleId={props.vehicleId} />,
      href: composePath(vehiclesRoutes.features, {params: {id: props.vehicleId}}),
      'data-testid': testIds.vehicles.detail('features'),
    })
    .when((isServiceVehicle || isRentalVehicle) && isWorkshopModuleEnabled, {
      id: 'serviceIntervals',
      title: i18n.t('entity.vehicle.labels.serviceIntervals'),
      content: (
        <VehicleServiceIntervals
          data-testid={testIds.vehicles.detail('serviceIntervals')}
          vehicleId={props.vehicleId}
        />
      ),
      href: composePath(vehiclesRoutes.serviceIntervals, {params: {id: props.vehicleId}}),
      'data-testid': testIds.vehicles.detail('serviceIntervals'),
    })
    .when(isRentalVehicle, {
      id: 'rentalVehicle',
      title: i18n.t('entity.vehicle.labels.rentalInformation'),
      content: <VehicleRental vehicleId={props.vehicleId} maximization={props.maximization} />,
      href: composePath(vehiclesRoutes.rental, {params: {id: props.vehicleId}}),
      'data-testid': testIds.vehicles.detail('rentalVehicle'),
    })
    .when(isServiceVehicle && !isNilOrEmpty(additionalInformation), {
      id: 'additionalInformation',
      title: i18n.t('entity.vehicle.labels.additionalInformation'),
      content: <VehicleAdditionalInformation vehicleId={props.vehicleId} />,
      href: composePath(vehiclesRoutes.additionalInformation, {params: {id: props.vehicleId}}),
      'data-testid': testIds.vehicles.detail('additionalInformation'),
    });

  return <Main data-testid={testIds.vehicles.detail('main')} tabs={navigationItems} />;
}
